import { z } from "zod";
import { clubSportIdSchema } from "@/common/utils/club-sport";

export const loginSchema = z.object({
  email: z.string().email("Invalid email format"),
  password: z.string().min(1, "Password is required"),
});

export const logoutSchema = z.object({
  refreshToken: z.string().min(1, "Refresh token is required"),
});

export const googleLoginSchema = z.object({
  idToken: z.string().min(1, "Google ID token is required"),
});

export const magicLinkRequestSchema = z.object({
  email: z.string().email("Invalid email format"),
  // Client-side navigation hint only — validated again server-side
  // (`isSafeInternalRedirect`) and silently dropped there if unsafe rather
  // than erroring. Never round-tripped through the JWT or used for anything
  // authorization-relevant.
  redirect: z.string().optional().nullable(),
});

export const magicLinkVerifySchema = z.object({
  token: z.string().min(1, "Token is required"),
});

export const refreshTokenSchema = z.object({
  refreshToken: z.string().min(1, "Refresh token is required"),
});

export const registerClubSchema = z.object({
  clubName: z.string().min(1, "Club name is required"),
  clubDescription: z.string().optional(),
  clubPhoneNumber: z.string().optional(),
  clubWebsite: z.string().url("Must be a valid URL").optional().or(z.literal("")),
  // Required on this path too: registration and onboarding both create a
  // club, and the API validates the sport identically on all three.
  // `onboardClubSchema` below picks this field, so both flows inherit it.
  clubSportId: clubSportIdSchema,
  competitionId: z.string().optional(),
  seasonId: z.string().optional(),
  coachName: z.string().min(1, "Name is required"),
  coachEmail: z.string().email("Invalid email format"),
  coachPassword: z.string().min(8, "Password must be at least 8 characters"),
  recaptchaToken: z.string().optional(),
});

export const registerPlayerSchema = z.object({
  name: z.string().min(1, "Name is required"),
  email: z.string().email("Invalid email format"),
  password: z.string().min(8, "Password must be at least 8 characters"),
  club_id: z.string().optional(),
  team_id: z.string().optional(),
  recaptchaToken: z.string().optional(),
});

export const onboardClubSchema = registerClubSchema.pick({
  clubName: true,
  clubDescription: true,
  clubPhoneNumber: true,
  clubWebsite: true,
  clubSportId: true,
  competitionId: true,
  seasonId: true,
});

export const updateProfileSchema = z.object({
  name: z
    .string()
    .min(2, "Name must be at least 2 characters")
    .max(100, "Name cannot exceed 100 characters"),
  email: z.string().email("Invalid email format"),
});

export const updatePasswordSchema = z
  .object({
    currentPassword: z.string().min(1, "Current password is required"),
    newPassword: z
      .string()
      .min(8, "Password must be at least 8 characters")
      .regex(
        /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]).+$/,
        "Password must contain at least one uppercase letter, one lowercase letter, one number, and one special character"
      ),
    confirmPassword: z.string().min(1, "Please confirm your new password"),
  })
  .refine((data) => data.newPassword === data.confirmPassword, {
    message: "Passwords do not match",
    path: ["confirmPassword"],
  });

export type TLoginRequest = z.infer<typeof loginSchema>;
export type TLogoutRequest = z.infer<typeof logoutSchema>;
export type TGoogleLoginRequest = z.infer<typeof googleLoginSchema>;
export type TMagicLinkRequest = z.infer<typeof magicLinkRequestSchema>;
export type TMagicLinkVerifyRequest = z.infer<typeof magicLinkVerifySchema>;
export type TRefreshTokenRequest = z.infer<typeof refreshTokenSchema>;
export type TRegisterClubRequest = z.infer<typeof registerClubSchema>;
export type TRegisterPlayerRequest = z.infer<typeof registerPlayerSchema>;
export type TOnboardClubRequest = z.infer<typeof onboardClubSchema>;
export type TUpdateProfileRequest = z.infer<typeof updateProfileSchema>;
export type TUpdatePasswordRequest = z.infer<typeof updatePasswordSchema>;
